import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Receipt } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { useToast } from '@/components/toast';
import { InvoicePayment } from '@/components/invoice-payment';
import { usePortalInvoice, usePortalInvoices } from '@/lib/portal-invoices';
import { useHasPermission } from '@/lib/permissions';
import { dollars, formatDate } from '@/lib/format';
import type { InvoiceData } from '@/types/api';

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Maps an invoice to a StatusPill tone, surfacing overdue as danger. */
function invoiceTone(invoice: InvoiceData): Tone {
  if (invoice.is_overdue && invoice.status !== 'paid' && invoice.status !== 'void') {
    return 'danger';
  }
  switch (invoice.status) {
    case 'paid':
      return 'success';
    case 'partial':
      return 'warning';
    case 'sent':
      return 'info';
    default:
      return 'neutral';
  }
}

function invoiceLabel(invoice: InvoiceData): string {
  if (invoice.is_overdue && invoice.status !== 'paid' && invoice.status !== 'void') {
    return 'Overdue';
  }
  return invoice.status_label;
}

export function PortalInvoices() {
  const { data, isLoading } = usePortalInvoices();
  const navigate = useNavigate();
  const rows = data?.data;

  return (
    <div className="space-y-5">
      <PageHeader icon={Receipt} title="Invoices" subtitle="View and pay your invoices." />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No invoices yet"
          description="Invoices from Mehrutherm will appear here when they're issued."
        />
      ) : (
        <DataList>
          {rows.map((invoice) => (
            <DataListRow
              key={invoice.id}
              onClick={() => navigate(`/invoices/${invoice.id}`)}
              interactive
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{invoice.number}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {invoice.due_date ? `Due ${formatDate(invoice.due_date)}` : `Issued ${formatDate(invoice.issue_date)}`}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-medium tabular-nums text-foreground">
                  {dollars(invoice.total_cents)}
                </div>
                {invoice.balance_cents > 0 && invoice.balance_cents !== invoice.total_cents ? (
                  <div className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                    {dollars(invoice.balance_cents)} due
                  </div>
                ) : null}
              </div>
              <StatusPill tone={invoiceTone(invoice)}>{invoiceLabel(invoice)}</StatusPill>
            </DataListRow>
          ))}
        </DataList>
      )}
    </div>
  );
}

export function PortalInvoiceDetail() {
  const { id } = useParams();
  const invoiceId = id ? Number(id) : undefined;
  const { data: invoice, isLoading } = usePortalInvoice(invoiceId);
  const navigate = useNavigate();
  const toast = useToast();
  const canPay = useHasPermission('invoices.pay');
  const [payOpen, setPayOpen] = useState(false);

  if (isLoading || !invoice) {
    return <SkeletonList rows={5} />;
  }

  const canPayThis = canPay && invoice.balance_cents > 0 && invoice.status !== 'void';

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <button
        type="button"
        onClick={() => navigate('/invoices')}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]"
      >
        <ArrowLeft className="size-4" /> Back to invoices
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{invoice.number}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
            <StatusPill tone={invoiceTone(invoice)}>{invoiceLabel(invoice)}</StatusPill>
            <span>Issued {formatDate(invoice.issue_date)}</span>
            {invoice.due_date ? <span>· Due {formatDate(invoice.due_date)}</span> : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums">{dollars(invoice.total_cents)}</div>
          {invoice.amount_paid_cents > 0 && invoice.status !== 'paid' ? (
            <div className="text-xs text-[var(--color-foreground-muted)]">
              {dollars(invoice.amount_paid_cents)} paid · {dollars(invoice.balance_cents)} due
            </div>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      {canPayThis || invoice.receipt_url ? (
        <div className="flex flex-wrap items-center gap-2">
          {canPayThis ? (
            <Button onClick={() => setPayOpen(true)}>Pay {dollars(invoice.balance_cents)}</Button>
          ) : null}
          {invoice.receipt_url ? (
            <a href={invoice.receipt_url} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">
                <Download className="size-4" /> Receipt
              </Button>
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Line items */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Details</h2>
        </CardHeader>
        <CardBody className="!p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
                <th className="px-5 py-2 font-medium">Description</th>
                <th className="px-5 py-2 text-right font-medium">Qty</th>
                <th className="px-5 py-2 text-right font-medium">Unit</th>
                <th className="px-5 py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {invoice.line_items.map((line) => (
                <tr key={line.id}>
                  <td className="px-5 py-2.5">{line.description}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{line.quantity}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{dollars(line.unit_cents)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{dollars(line.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="space-y-1.5 border-t border-[var(--color-border)] px-5 py-4 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--color-foreground-muted)]">Subtotal</span>
              <span className="tabular-nums">{dollars(invoice.subtotal_cents)}</span>
            </div>
            {invoice.tax_cents > 0 ? (
              <div className="flex justify-between">
                <span className="text-[var(--color-foreground-muted)]">
                  Tax ({(invoice.tax_rate_bp / 100).toFixed(2)}%)
                </span>
                <span className="tabular-nums">{dollars(invoice.tax_cents)}</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t border-[var(--color-border)] pt-1.5 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{dollars(invoice.total_cents)}</span>
            </div>
            {invoice.amount_paid_cents > 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-[var(--color-foreground-muted)]">Paid</span>
                  <span className="tabular-nums">-{dollars(invoice.amount_paid_cents)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Balance due</span>
                  <span className="tabular-nums">{dollars(invoice.balance_cents)}</span>
                </div>
              </>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {invoice.notes ? (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Notes</h2>
          </CardHeader>
          <CardBody>
            <p className="whitespace-pre-line text-sm text-[var(--color-foreground)]">{invoice.notes}</p>
          </CardBody>
        </Card>
      ) : null}

      {/* Payment dialog */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title={`Pay ${invoice.number}`}
        size="md"
      >
        <InvoicePayment
          invoiceId={invoice.id}
          onCancel={() => setPayOpen(false)}
          onDone={() => {
            setPayOpen(false);
            toast.success('Payment received', "We're confirming it now. Your receipt will follow by email.");
          }}
        />
      </Dialog>
    </div>
  );
}
